"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Testimonials() {
  const testimonials = [{
    quote: "AutolexaAi completely revolutionized our workflow. The automation efficiency is truly unmatched.",
    highlight: "revolutionized our workflow",
    author: "Sarah Jenkins",
    role: "Operations Director",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_sarah_jenkins.webp",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_sarah_jenkins.webp-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_sarah_jenkins.webp-wvc-sizes"
  }, {
    quote: "A seamless integration that saved our team hours of manual data entry every single week.",
    highlight: "seamless integration",
    author: "Marcus Thorne",
    role: "Founder, TechFlow",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_marcus_thorne.webp",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_marcus_thorne.webp-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_marcus_thorne.webp-wvc-sizes"
  }, {
    quote: "The AI chatbot implementation has transformed our customer support into a 24/7 powerhouse.",
    highlight: "transformed our customer support",
    author: "Elena Rodriguez",
    role: "Customer Success Lead",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_elena_rodriguez.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_elena_rodriguez.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_elena_rodriguez.png-wvc-sizes"
  }, {
    quote: "AutolexaAi is not just a service, it is a strategic partner in our digital transformation journey.",
    highlight: "strategic partner",
    author: "David Chen",
    role: "CEO, Innovate Corp",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_david_chen.webp",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_david_chen.webp-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/client_david_chen.webp-wvc-sizes"
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const changeSlide = (next: number) => {
    setIsFading(true);
    setTimeout(() => {
      setActiveIndex((next + testimonials.length) % testimonials.length);
      setIsFading(false);
    }, 400);
  };
  useEffect(() => {
    const interval = setInterval(() => {
      changeSlide(activeIndex + 1);
    }, 8000);
    return () => clearInterval(interval);
  }, [activeIndex]);
  const active = testimonials[activeIndex];
  const renderQuoteWithHighlight = (quote: string, highlight: string) => {
    if (!highlight || !quote.includes(highlight)) {
      return <span>{quote}</span>;
    }
    const parts = quote.split(highlight);
    return <>
        {parts[0]}
        <span style={{
        color: "var(--foreground)"
      }}>{highlight}</span>
        {parts[1]}
      </>;
  };
  return <section data-section-id="5186" id="client-success-stories" className="bg-background text-foreground relative py-24 md:py-32 lg:py-[140px]">
      <div className="absolute top-0 left-0 right-0 h-px" style={{
      backgroundColor: "var(--border)"
    }} />
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{
      backgroundColor: "var(--border)"
    }} />

      <div ref={ref} className={`mx-auto max-w-7xl px-6 md:px-10 lg:px-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-x-8">
          {}
          <div className="lg:col-span-2">
            <p className="font-default text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
              Client
              <br className="hidden lg:block" />
              <span className="lg:hidden"> </span>
              Success Stories
            </p>
          </div>

          {}
          <div className="lg:col-span-8 lg:col-start-3">
            <div className={`transition-opacity duration-[800ms] ease-in-out ${isFading ? "opacity-0" : "opacity-100"}`}>
              {}
              <div className="relative">
                <span aria-hidden="true" className="font-default absolute -top-8 md:-top-12 lg:-top-16 -left-2 md:-left-4 lg:-left-6 leading-none select-none pointer-events-none" style={{
                fontSize: "clamp(96px, 14vw, 160px)",
                color: "var(--foreground)",
                opacity: 0.15
              }}>
                  &ldquo;
                </span>

                <blockquote className="font-default italic text-foreground relative z-10 max-w-[620px]" style={{
                fontSize: "clamp(24px, 3vw, 36px)",
                lineHeight: 1.4
              }}>
                  {renderQuoteWithHighlight(active.quote, active.highlight)}
                </blockquote>
              </div>

              {}
              <div className="mt-10 h-px w-10" style={{
              backgroundColor: "var(--foreground)"
            }} />

              {}
              <div className="mt-6 flex items-center gap-4">
                <div className="overflow-hidden rounded-lg shrink-0" style={{
                width: "56px",
                height: "70px"
              }}>
                  <img src={active.avatar} data-wvc-srcset={active.avatarSrcset} data-wvc-sizes={active.avatarSizes} alt={active.author} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>
                <div className="flex flex-col">
                  <p className="font-default text-foreground" style={{
                  fontSize: "18px"
                }}>
                    {active.author}
                  </p>
                  <p className="font-default text-[10px] tracking-[0.18em] uppercase text-muted-foreground mt-1">
                    {active.role}
                  </p>
                </div>
              </div>
            </div>

            {}
            <div className="mt-16 flex items-center justify-between border-t pt-6" style={{
            borderColor: "var(--border)"
          }}>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" aria-label="Previous testimonial" onClick={() => changeSlide(activeIndex - 1)} className="rounded-lg hover:bg-accent transition-transform duration-300 hover:-translate-x-1">
                  <ChevronLeft className="size-5" strokeWidth={1.25} />
                </Button>
                <Button variant="ghost" size="icon" aria-label="Next testimonial" onClick={() => changeSlide(activeIndex + 1)} className="rounded-lg hover:bg-accent transition-transform duration-300 hover:translate-x-1">
                  <ChevronRight className="size-5" strokeWidth={1.25} />
                </Button>
              </div>

              <p className="font-default text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
                <span className="text-foreground">
                  {String(activeIndex + 1).padStart(2, "0")}
                </span>
                <span className="mx-1.5 opacity-60">/</span>
                <span>{String(testimonials.length).padStart(2, "0")}</span>
              </p>
            </div>

            {}
            <div className="mt-6 flex items-center gap-2">
              {testimonials.map((_, i) => <Button key={i} data-index={i} variant="ghost" aria-label={`Go to testimonial ${i + 1}`} onClick={() => {
              if (i !== activeIndex) changeSlide(i);
            }} className="p-0 h-px transition-all duration-500" style={{
              width: i === activeIndex ? "40px" : "16px",
              backgroundColor: i === activeIndex ? "var(--foreground)" : "var(--border)"
            }} />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}